import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ShoppingCart, User2Icon, X } from "lucide-react";
import axios from "axios";
import { useAppContext } from "../context";
import CustomerNotificationBell from "./CustomerNotificationBell";
import { apiUrl } from "../utils/config";

const Header = () => {
  const { setIsCartOpen, cartItemsCount, isAuthenticated, customerUser, storeSettings } = useAppContext();
  const storeName = storeSettings?.store_name || "Gamapa Foods";
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navLinks, setNavLinks] = useState([]);

  useEffect(() => {
    axios
      .get(`${apiUrl}/nav-links/public?location=header`)
      .then((res) => setNavLinks(res.data?.list || []))
      .catch(() => {});
  }, []);

  if (location.pathname.includes("admin")) {
    return null;
  }

  const handleNavClick = (url) => {
    setMobileOpen(false);
    if (url.startsWith("http")) {
      window.open(url, "_blank", "noopener noreferrer");
    } else {
      navigate(url);
    }
  };

  return (
    <nav className="sticky top-0 z-40 border-b border-gray-100 bg-white/90 shadow-sm backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex h-16 items-center justify-between gap-4">
          <button
            className="flex items-center gap-2"
            onClick={() => navigate("/")}
            type="button"
          >
            <div className="rounded-lg bg-orange-500 p-2 text-white">
              <ShoppingCart size={20} />
            </div>
            <span className="text-xl font-bold tracking-tight text-gray-900">
              {storeName.toUpperCase()}
            </span>
          </button>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) =>
              link.open_in_new_tab ? (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium transition hover:text-orange-500"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.id}
                  className="text-sm font-medium transition hover:text-orange-500"
                  to={link.url}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* Right-side icons */}
          <div className="flex items-center gap-3">
            {isAuthenticated && customerUser?.id && (
              <CustomerNotificationBell customerId={customerUser.id} />
            )}

            <button className="relative" onClick={() => setIsCartOpen(true)} type="button">
              <ShoppingCart className="h-6 w-6 text-gray-600 transition hover:text-orange-500" />
              {cartItemsCount > 0 ? (
                <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white shadow-md">
                  {cartItemsCount}
                </span>
              ) : null}
            </button>

            <button
              className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition ${
                isAuthenticated
                  ? "border-orange-200 bg-orange-50 text-orange-600 hover:bg-orange-100"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => navigate(isAuthenticated ? "/profile" : "/login")}
              type="button"
            >
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full ${
                  isAuthenticated ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600"
                }`}
              >
                <User2Icon size={16} />
              </span>
              {isAuthenticated ? <span className="hidden sm:inline">{customerUser?.firstName}</span> : null}
            </button>

            {/* Hamburger — mobile only */}
            <button
              className="flex items-center justify-center rounded-lg p-2 text-gray-600 transition hover:bg-gray-100 md:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              type="button"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-gray-100 bg-white md:hidden">
          <div className="mx-auto max-w-7xl px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  location.pathname === link.url
                    ? "bg-orange-50 text-orange-600"
                    : "text-gray-700 hover:bg-gray-50 hover:text-orange-500"
                }`}
                onClick={() => handleNavClick(link.url)}
                type="button"
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
